import { Button } from '@boostbrothers/web-ui/web';
import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';

import useImageMutation from '../hooks/useImageMutation';

interface FormThumbnailProps {
  fromPA: boolean;
  thumbnail: { url: string; fileName: string };
  setThumbnail: (thumbnail: { url: string; fileName: string }) => void;
  handleTemporarySave: () => void;
}

const FormThumbnail = ({
  fromPA,
  thumbnail,
  setThumbnail,
  handleTemporarySave,
}: FormThumbnailProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    uploadImage,
    isUploading,
    imageFile: thumbnailFile,
    setImageFile: setThumbnailFile,
  } = useImageMutation({ handleTemporarySave, fromPA });

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    const file = files[0];
    if (file?.type.startsWith('image/')) {
      await uploadImage(file).then(url =>
        setThumbnail({ url, fileName: file.name })
      );
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadImage(file).then(url =>
        setThumbnail({ url, fileName: file.name })
      );
    }
  };

  const handleRemoveFile = () => {
    setThumbnailFile(null);
    setThumbnail({ url: '', fileName: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex shrink-0 flex-col gap-3">
      <div className="flex items-end justify-start gap-x-2">
        <span className="text-base text-CoolGray-900">썸네일 이미지</span>
        <span className="text-sm text-CoolGray-400">
          똑닥 앱 홈이나 SNS 링크를 공유할 때 대표 이미지로 보입니다.
        </span>
      </div>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex flex-col items-start justify-center gap-3 rounded-lg p-5 border-2 border-dashed transition-colors ${
          isDragging
            ? 'border-Primary-500 bg-Primary-50'
            : 'border-transparent bg-CoolGray-50'
        }`}
      >
        {thumbnailFile || thumbnail.url ? (
          <div className="flex flex-col items-start justify-center gap-3">
            <div className="relative w-[200px] h-[150px] overflow-hidden rounded-md bg-CoolGray-100">
              <img
                src={
                  thumbnailFile
                    ? URL.createObjectURL(thumbnailFile)
                    : thumbnail.url
                }
                alt="썸네일 미리보기"
                className={`w-full h-full object-cover object-center ${
                  isUploading ? 'opacity-50' : ''
                }`}
              />
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-Primary-500">
                    업로드 중...
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1">
              {thumbnailFile && (
                <span className="text-sm text-CoolGray-700 truncate max-w-[200px]">
                  {thumbnailFile.name}
                </span>
              )}
              <div className="flex items-center gap-x-2">
                <Button
                  label="이미지 업로드"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                />
                <Button
                  label="삭제"
                  onClick={handleRemoveFile}
                  colorType="white"
                  className="text-red-500"
                  disabled={isUploading}
                />
              </div>
            </div>
          </div>
        ) : (
          <Button
            label="이미지 업로드"
            onClick={() => fileInputRef.current?.click()}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          id="thumbnail"
          accept="image/*"
          onChange={handleFileChange}
          className="sr-only"
        />
        <div className="text-sm text-CoolGray-500 mt-4">
          <p>- 권장 사이즈: 1000 x 750 (px)</p>
          <p>- 권장 용량: 최대 10MB</p>
        </div>
      </div>
    </div>
  );
};

export default FormThumbnail;
